import { createContext, useContext, useState, useCallback, useEffect } from "react";
import {
  listTaxProfiles,
  getTaxProfileQuota,
  getCurrentTaxProfile,
} from "@/api/taxProfileService";
import {
  getActiveProfileId,
  setActiveProfileId,
  clearActiveProfileId,
} from "@/lib/profileStorage";
import { useUser } from "./UserContext";
import { getAuthToken } from "@/lib/authStorage";

const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const { user, setAccountType } = useUser();
  const [profiles, setProfiles] = useState([]);
  const [quota, setQuota] = useState({ allowedProfiles: 1, used: 0 });
  const [activeProfile, setActiveProfile] = useState(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const refreshProfiles = useCallback(async () => {
    if (!getAuthToken() || !user) {
      setProfiles([]);
      setActiveProfile(null);
      return { profiles: [], quota: null, active: null };
    }
    setLoadingProfiles(true);
    try {
      const [list, q] = await Promise.all([
        listTaxProfiles(),
        getTaxProfileQuota(),
      ]);
      const profileList = Array.isArray(list) ? list : list?.profiles || [];
      setProfiles(profileList);
      setQuota(q || { allowedProfiles: 1, used: profileList.length });

      // No default profile: only a selection made in this tab counts.
      // Expired profiles cannot remain active until renewed.
      const storedId = getActiveProfileId();
      const active =
        profileList.find(
          (p) =>
            String(p._id) === String(storedId) &&
            !p.isExpired
        ) || null;

      if (active) {
        setActiveProfile(active);
        setAccountType?.(active.accountType || "none");
      } else {
        clearActiveProfileId();
        setActiveProfile(null);
        setAccountType?.("none");
      }
      return { profiles: profileList, quota: q, active };
    } catch (e) {
      console.error("Error loading profiles", e);
      return { profiles: [], quota: null, active: null };
    } finally {
      setLoadingProfiles(false);
      setHasLoadedOnce(true);
    }
  }, [user, setAccountType]);

  const selectProfile = useCallback(
    async (profileOrId) => {
      const candidate =
        typeof profileOrId === "string"
          ? profiles.find((p) => String(p._id) === profileOrId)
          : profileOrId;
      const id = String(candidate?._id || profileOrId || "");
      if (!id) return null;
      if (candidate?.isExpired) {
        throw new Error(
          "Este perfil ha expirado. Renuévalo para continuar."
        );
      }
      setActiveProfileId(id);
      const fromList = profiles.find((p) => String(p._id) === id);
      if (fromList) {
        if (fromList.isExpired) {
          throw new Error(
            "Este perfil ha expirado. Renuévalo para continuar."
          );
        }
        setActiveProfile(fromList);
        setAccountType?.(fromList.accountType || "none");
        return fromList;
      }
      try {
        const current = await getCurrentTaxProfile();
        setActiveProfile(current);
        setAccountType?.(current?.accountType || "none");
        return current;
      } catch (e) {
        if (e?.response?.status === 402) {
          clearActiveProfileId();
          setActiveProfile(null);
          setAccountType?.("none");
        }
        throw e;
      }
    },
    [profiles, setAccountType]
  );

  const clearProfile = useCallback(() => {
    clearActiveProfileId();
    setActiveProfile(null);
    setAccountType?.("none");
  }, [setAccountType]);

  const clearProfileSession = useCallback(() => {
    clearActiveProfileId();
    setActiveProfile(null);
    setProfiles([]);
    setHasLoadedOnce(false);
  }, []);

  useEffect(() => {
    if (user) refreshProfiles();
    else clearProfileSession();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onLogout = () => clearProfileSession();
    window.addEventListener("user:logout", onLogout);
    return () => window.removeEventListener("user:logout", onLogout);
  }, [clearProfileSession]);

  return (
    <ProfileContext.Provider
      value={{
        profiles,
        quota,
        activeProfile,
        loadingProfiles,
        hasLoadedOnce,
        refreshProfiles,
        selectProfile,
        clearProfile,
        hasProfiles: profiles.length > 0,
        needsProfileSelection:
          !!user && hasLoadedOnce && profiles.length > 0 && !activeProfile,
        slotsAvailable:
          (quota?.allowedProfiles || 0) - (quota?.used ?? profiles.length),
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error("useProfile must be used within ProfileProvider");
  }
  return ctx;
};
